import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { DndContext, DragOverlay, closestCenter, pointerWithin, defaultDropAnimationSideEffects } from '@dnd-kit/core'
import type { DragStartEvent, DragEndEvent, CollisionDetection, DropAnimation } from '@dnd-kit/core'
import { Loader2, X } from 'lucide-react'
import { usePageBuilder } from '../../hooks'
import { BuilderToolbar } from './BuilderToolbar'
import { BuilderTabs } from './BuilderTabs'
import { ComponentLibrary } from './ComponentLibrary'
import { BuilderCanvas } from './BuilderCanvas'
import { PropertiesPanel } from './PropertiesPanel'
import { BlockPreview } from './blocks/BlockPreview'
import { LayoutSelector } from './LayoutSelector'
import { BLOCK_CATALOG } from './blocks/block-catalog'
import type { BuilderTabType } from './BuilderTabs'
import type { BlockType, PageBlock } from '../../types'
import { DEVICE_WIDTHS } from '../../types'

/** Smooth snap-back for palette items dropped outside the canvas */
const snapBackAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.5' } },
  }),
  duration: 300,
  easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
}

/** Subtle settle for canvas reorder drops */
const settleAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.5' } },
  }),
  duration: 200,
  easing: 'ease',
}

/** Custom collision: palette drops require pointer inside canvas; canvas reorder uses closestCenter */
const canvasCollision: CollisionDetection = (args) => {
  const isPaletteDrag = String(args.active.id).startsWith('palette-')

  if (isPaletteDrag) {
    // Palette → Canvas: pointer must be physically inside the canvas drop zone
    return pointerWithin(args)
  }

  // Canvas reorder: closestCenter for smooth block-to-block sorting
  return closestCenter(args)
}

export function BuilderPage() {
  const { id: funnelId, stepId } = useParams<{ id: string; stepId: string }>()
  const [activeTab, setActiveTab] = useState<BuilderTabType>('upsells')
  const [activeId, setActiveId] = useState<string | null>(null)
  const [templateSelected, setTemplateSelected] = useState(false)
  const dropAnimationRef = useRef<DropAnimation | null>(settleAnimation)

  const {
    blocks,
    setBlocks,
    selectedBlockId,
    setSelectedBlockId,
    showPreview,
    setShowPreview,
    devicePreview,
    setDevicePreview,
    saveStatus,
    loading,
    addBlock,
    duplicateBlock,
    deleteBlock,
    updateBlockProps,
    reorderBlocks,
    handleSave,
    exportJson,
    undo,
    redo,
    canUndo,
    canRedo,
  } = usePageBuilder(funnelId, stepId)

  // Once API load completes, skip the template selector and go straight to builder
  useEffect(() => {
    if (!loading) setTemplateSelected(true)
  }, [loading])

  // Close preview overlay on Escape — attached to document so it works
  // regardless of which element has focus (same pattern as AlertsBell)
  useEffect(() => {
    if (!showPreview) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowPreview(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [showPreview, setShowPreview])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = event.active.id as string
    setActiveId(id)
    // Palette drags default to snap-back (overridden to null on successful drop)
    dropAnimationRef.current = id.startsWith('palette-') ? snapBackAnimation : settleAnimation
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      const activeData = active.data.current as { fromPalette?: boolean; type?: BlockType } | undefined

      // Palette → Canvas: create new block (only if dropped on canvas)
      if (activeData?.fromPalette && activeData.type) {
        if (!over) {
          // Dropped outside canvas — snap-back animation (already set in handleDragStart)
          setActiveId(null)
          return
        }
        // Successful palette drop — no overlay animation, block appears instantly
        dropAnimationRef.current = null
        setActiveId(null)
        const config = BLOCK_CATALOG.find((b) => b.type === activeData.type)
        if (!config) return
        const newBlock: PageBlock = {
          id: `block-${Date.now()}`,
          type: activeData.type,
          props: { ...config.defaultProps },
        }
        const overId = String(over.id)
        setBlocks((items) => {
          // Dropped on a gap indicator → insert at that index
          if (overId.startsWith('gap-')) {
            const insertIndex = parseInt(overId.replace('gap-', ''), 10)
            const updated = [...items]
            updated.splice(insertIndex, 0, newBlock)
            return updated
          }
          // Dropped on a block → insert after it
          const overIndex = items.findIndex((b) => b.id === overId)
          if (overIndex >= 0) {
            const updated = [...items]
            updated.splice(overIndex + 1, 0, newBlock)
            return updated
          }
          // Dropped on canvas-drop-zone → append
          return [...items, newBlock]
        })
        setSelectedBlockId(newBlock.id)
        return
      }

      // Canvas reorder — settleAnimation already set in handleDragStart
      setActiveId(null)
      if (over && active.id !== over.id) {
        reorderBlocks(active.id as string, over.id as string)
      }
    },
    [setBlocks, setSelectedBlockId, reorderBlocks],
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  const selectedBlock = useMemo(
    () => blocks.find((b) => b.id === selectedBlockId) || null,
    [blocks, selectedBlockId],
  )

  // Drag overlay content
  const activeBlock = useMemo(
    () => blocks.find((b) => b.id === activeId),
    [blocks, activeId],
  )
  const activePaletteType = useMemo(
    () => activeId?.startsWith('palette-')
      ? BLOCK_CATALOG.find((c) => `palette-${c.type}` === activeId)
      : null,
    [activeId],
  )

  if (loading) {
    return (
      <div className="funnels-theme h-[calc(100vh-80px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-default-400" />
        <span className="ml-2 text-default-500">Loading page content...</span>
      </div>
    )
  }

  // Show layout selector only when there are no blocks and user hasn't passed through loading
  if (blocks.length === 0 && !templateSelected) {
    return (
      <div className="funnels-theme h-[calc(100vh-80px)]">
        <LayoutSelector onSelectTemplate={(templateBlocks) => {
          setTemplateSelected(true)
          setBlocks(templateBlocks)
        }} />
      </div>
    )
  }

  return (
    <div className="funnels-theme h-[calc(100vh-80px)] flex flex-col">
      <BuilderToolbar
        funnelId={funnelId}
        stepId={stepId}
        blockCount={blocks.length}
        saveStatus={saveStatus}
        showPreview={showPreview}
        devicePreview={devicePreview}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        onSave={handleSave}
        onExport={exportJson}
        onTogglePreview={() => setShowPreview(!showPreview)}
        onDeviceChange={setDevicePreview}
      />
      <BuilderTabs activeTab={activeTab} onChange={setActiveTab} />

      <div className="flex-1 flex overflow-hidden">
        <DndContext collisionDetection={canvasCollision} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
          {!showPreview && (
            <ComponentLibrary
              onAddBlock={(type) => {
                const config = BLOCK_CATALOG.find((b) => b.type === type)
                if (config) addBlock(type, config.defaultProps)
              }}
            />
          )}

          <BuilderCanvas
            blocks={blocks}
            selectedBlockId={selectedBlockId}
            devicePreview={devicePreview}
            showPreview={showPreview}
            isPaletteDragging={activeId?.startsWith('palette-') ?? false}
            onSelectBlock={setSelectedBlockId}
            onDeleteBlock={deleteBlock}
            onDuplicateBlock={duplicateBlock}
          />

          <DragOverlay dropAnimation={dropAnimationRef.current}>
            {activeBlock ? (
              <div className="bg-tf-bg-card border-2 border-primary rounded-lg p-3 shadow-lg opacity-90 max-w-[200px]">
                <BlockPreview block={activeBlock} />
              </div>
            ) : activePaletteType ? (
              <div className="bg-tf-bg-card border-2 border-primary rounded-lg px-3 py-2 shadow-lg opacity-90">
                <p className="text-sm font-medium text-primary">{activePaletteType.label}</p>
                <p className="text-xs text-tf-text-muted">{activePaletteType.description}</p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {!showPreview && <PropertiesPanel selectedBlock={selectedBlock} onUpdateProps={updateBlockProps} />}
      </div>

      {/* Preview overlay dialog — backdrop click dismisses, inner card stops propagation */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setShowPreview(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Page preview"
        >
          <div
            className={`relative ${DEVICE_WIDTHS[devicePreview]} w-full mx-4 bg-content1 rounded-xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-default-200 bg-content2 shrink-0">
              <span className="text-sm font-medium text-default-600">Preview — {devicePreview}</span>
              <div className="flex items-center gap-2">
                {(['desktop', 'tablet', 'mobile'] as const).map((device) => (
                  <button
                    key={device}
                    className={`px-2 py-1 text-xs rounded ${devicePreview === device ? 'bg-primary text-white' : 'text-default-500 hover:bg-default-100'}`}
                    onClick={() => setDevicePreview(device)}
                  >
                    {device.charAt(0).toUpperCase() + device.slice(1)}
                  </button>
                ))}
                <button
                  onClick={() => setShowPreview(false)}
                  className="ml-2 p-1 rounded hover:bg-default-200 text-default-500 hover:text-default-700 transition-colors"
                  aria-label="Close preview"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {blocks.map((block) => (
                <BlockPreview key={block.id} block={block} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
